import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Col, Form, Input, InputNumber, message, Modal, Row, Select, Space, Switch, Tag
} from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { categoryApi } from './itemConfigApi';
import { CATEGORY_TYPE_OPTIONS, CATEGORY_TYPE_COLOR } from './itemConfigConstants';
import '../../../../styles/erpModal.css';

// ─────────────────────────────────────────────────────────────────
// Glass-i — Item Category modal (Stage 6B.8A-2A).
//
// Compact AntD Modal — same ultra-compact rhythm as UomModal:
//   - 720 px width
//   - header chips for System / Active
//   - inline lock hint (no big banner)
//   - 2-column sectioned grid
//   - parent picker pulls from the category lookup endpoint (limit 200)
// ─────────────────────────────────────────────────────────────────

const initialForm = () => ({
  categoryCode:     '',
  categoryName:     '',
  parentCategoryId: null,
  categoryType:     'erp',
  sortOrder:        null,
  remarks:          '',
  isActive:         true
});

function unwrapApiError(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function SectionTitle({ children }) {
  return (
    <div style={{
      margin: '2px 0 6px', borderBottom: '1px solid #f0f0f0', paddingBottom: 4,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: '#6b7280'
    }}>{children}</div>
  );
}

function InlineHint({ icon, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 6,
      fontSize: 11.5, color: '#6b7280', lineHeight: 1.5,
      margin: '-2px 0 8px'
    }}>
      {icon ? <span style={{ marginTop: 1 }}>{icon}</span> : null}
      <span>{children}</span>
    </div>
  );
}

export default function ItemCategoryModal({ open, mode = 'add', record = null, onClose, onSaved }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [parents, setParents] = useState([]);
  const isEdit = mode === 'edit';
  const isView = mode === 'view';
  const readOnly = isView;
  const isSystem = (isEdit || isView) && record?.isSystem === true;
  const codeLocked = readOnly || isSystem;

  // Load parent choices lazily when the modal opens (active rows).
  useEffect(() => {
    if (!open) return;
    let abort = false;
    categoryApi.lookup({ limit: 200 })
      .then((rows) => { if (!abort) setParents(rows || []); })
      .catch(() => { if (!abort) setParents([]); });
    return () => { abort = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (mode === 'add' || !record) {
      form.resetFields();
      form.setFieldsValue(initialForm());
    } else {
      form.setFieldsValue({
        categoryCode:     record.categoryCode || '',
        categoryName:     record.categoryName || '',
        parentCategoryId: record.parentCategoryId || null,
        categoryType:     record.categoryType || 'erp',
        sortOrder:        Number.isFinite(record.sortOrder) ? record.sortOrder : null,
        remarks:          record.remarks || '',
        isActive:         record.isActive !== false
      });
    }
  }, [open, mode, record, form]);

  const onSubmit = useCallback(async () => {
    if (readOnly) return onClose?.();
    let values;
    try {
      values = await form.validateFields();
    } catch (_e) { return; }
    setBusy(true);
    try {
      if (isEdit && record?.id) {
        const payload = {
          categoryName:     values.categoryName,
          parentCategoryId: values.parentCategoryId || null,
          categoryType:     values.categoryType,
          sortOrder:        values.sortOrder,
          remarks:          values.remarks || null,
          isActive:         values.isActive !== false
        };
        // Only send code on non-system rows; server rejects it on system rows anyway.
        if (!isSystem) payload.categoryCode = values.categoryCode;
        await categoryApi.update(record.id, payload);
        message.success('Category updated.');
      } else {
        const payload = {
          categoryCode:     String(values.categoryCode || '').trim().toUpperCase(),
          categoryName:     String(values.categoryName || '').trim(),
          parentCategoryId: values.parentCategoryId || null,
          categoryType:     values.categoryType,
          sortOrder:        values.sortOrder,
          remarks:          values.remarks || null,
          isActive:         values.isActive !== false
        };
        await categoryApi.create(payload);
        message.success('Category created.');
      }
      onSaved?.();
    } catch (e) {
      message.error(unwrapApiError(e, isEdit ? 'Failed to update category.' : 'Failed to create category.'));
    } finally {
      setBusy(false);
    }
  }, [form, isEdit, readOnly, isSystem, record, onClose, onSaved]);

  const titleNode = useMemo(() => {
    const label = mode === 'add' ? 'New Category' : `Category — ${record?.categoryCode || ''}`;
    const chips = [];
    if (record?.isSystem) chips.push(<Tag key="sys" icon={<LockOutlined />} color="default" style={{ marginInlineEnd: 0 }}>System</Tag>);
    if (record) chips.push(
      <Tag key="active" color={record.isActive === false ? 'default' : 'success'} style={{ marginInlineEnd: 0 }}>
        {record.isActive === false ? 'Inactive' : 'Active'}
      </Tag>);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingRight: 28 }}>
        <span style={{ fontWeight: 600, fontSize: 15 }}>{label}</span>
        {chips.length ? <Space size={6} wrap>{chips}</Space> : null}
      </div>
    );
  }, [mode, record]);

  // Parent picker excludes the current row (server also blocks via CHECK).
  const parentOptions = useMemo(() => parents
    .filter((p) => !record || p.id !== record.id)
    .map((p) => ({
      value: p.id,
      label: (
        <span>
          <code style={{ fontWeight: 600 }}>{p.categoryCode}</code>
          <span style={{ color: '#6b7280', marginLeft: 6 }}>{p.categoryName}</span>
        </span>
      )
    })), [parents, record]);

  return (
    <Modal
      open={open}
      title={titleNode}
      onCancel={onClose}
      onOk={readOnly ? onClose : onSubmit}
      okText={readOnly ? 'Close' : isEdit ? 'Save' : 'Create'}
      okButtonProps={{ loading: busy }}
      cancelButtonProps={{ disabled: busy }}
      width={720}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-enterprise-modal"
    >
      {isSystem ? (
        <InlineHint icon={<LockOutlined style={{ color: '#6b7280' }} />}>
          Seeded system category. Code is locked; you can rename, regroup,
          re-type, and deactivate.
        </InlineHint>
      ) : null}

      <Form form={form} layout="vertical" size="middle" requiredMark="optional"
        disabled={readOnly} className="item-config-compact-form erp-enterprise-form">
        <style>{`
          .item-config-compact-form .ant-form-item { margin-bottom: 10px; }
          .item-config-compact-form .ant-form-item-label { padding-bottom: 2px; }
          .item-config-compact-form .ant-form-item-label > label { font-size: 12px; color: #475569; height: 22px; }
          .item-config-compact-form .ant-input,
          .item-config-compact-form .ant-input-number-input,
          .item-config-compact-form .ant-select-selector { font-size: 13px; }
          .item-config-compact-form .ant-form-item-explain { font-size: 11.5px; min-height: 16px; }
        `}</style>

        <SectionTitle>Basic</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Category Code" name="categoryCode"
              rules={[
                { required: true, message: 'Required' },
                { max: 40, message: 'Max 40 chars' },
                { pattern: /^[A-Za-z0-9_-]+$/, message: 'A-Z, 0-9, _, -' }
              ]}>
              <Input
                maxLength={40}
                disabled={codeLocked}
                placeholder="e.g. TOUGHENED_GLASS"
                suffix={codeLocked ? <LockOutlined style={{ color: '#9ca3af' }} /> : null}
                onChange={(e) => form.setFieldsValue({
                  categoryCode: e.target.value.toUpperCase()
                })}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={16}>
            <Form.Item label="Category Name" name="categoryName"
              rules={[
                { required: true, message: 'Required' },
                { max: 150, message: 'Max 150 chars' }
              ]}>
              <Input maxLength={150} placeholder="e.g. Toughened Glass" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Parent Category" name="parentCategoryId">
              <Select
                allowClear
                showSearch
                placeholder="Select parent (optional)"
                options={parentOptions}
                filterOption={(input, opt) => {
                  const txt = parents.find((p) => p.id === opt?.value);
                  if (!txt) return false;
                  return (txt.categoryCode + ' ' + txt.categoryName)
                    .toLowerCase().includes(input.toLowerCase());
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item label="Type" name="categoryType"
              rules={[{ required: true, message: 'Required' }]}>
              <Select
                options={CATEGORY_TYPE_OPTIONS.map((o) => ({
                  value: o.value,
                  label: <Tag color={CATEGORY_TYPE_COLOR[o.value]} style={{ marginInlineEnd: 0 }}>{o.label}</Tag>
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={4}>
            <Form.Item label="Active" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Col>
        </Row>

        <SectionTitle>Control</SectionTitle>
        <Row gutter={[10, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Sort Order" name="sortOrder">
              <InputNumber min={0} max={999999} precision={0}
                style={{ width: '100%' }} placeholder="e.g. 10" />
            </Form.Item>
          </Col>
          <Col xs={24}>
            <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 0 }}>
              <Input.TextArea rows={2} maxLength={2000}
                placeholder="Optional notes" />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
